import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  CalendarDays,
  Ticket,
  Inbox,
  Settings,
  LogOut,
  Menu,
  Bus,
  X,
  FileBarChart,
  MessageCircle,
  MessageCircleQuestion,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import NotificationsBell from "@/components/layout/NotificationsBell";
import IntegracionesIndicator from "@/components/layout/IntegracionesIndicator";
import ErrorBoundary from "@/components/ErrorBoundary";
import AvisosWhatsApp, { useAvisosWa } from "@/components/agente/AvisosWhatsApp";  // PROMPT 145

const NAV = [
  { to: "/", label: "Inicio", icon: LayoutDashboard, end: true, testid: "nav-inicio" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/reservas", label: "Reservas", icon: Ticket, testid: "nav-reservas" },
  { to: "/bandeja", label: "Bandeja a revisar", icon: Inbox, testid: "nav-bandeja" },
  { to: "/reportes", label: "Reportes", icon: FileBarChart, testid: "nav-reportes" },
  { to: "/agente", label: "Agente WhatsApp", icon: MessageCircle, testid: "nav-agente" },
  // PROMPT 132 (2026-10-16): las preguntas de siempre de los clientes y cómo contestarlas, con su propia pantalla.
  { to: "/preguntas", label: "Preguntas típicas", icon: MessageCircleQuestion, testid: "nav-preguntas" },
  { to: "/configuracion", label: "Configuración", icon: Settings, adminOnly: true, testid: "nav-configuracion" },
];

function SidebarContent({ onNavigate, isAdmin }) {
  // PROMPT 145: numero verde de conversaciones sin leer al lado de "Agente WhatsApp".
  const { total: noLeidos } = useAvisosWa();
  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white">
          <Bus className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-slate-900 text-sm">Tours NY</div>
          <div className="text-[11px] text-slate-500 tracking-wide">· Reservas</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.filter((n) => !n.adminOnly || isAdmin).map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-slate-100 text-slate-900 font-semibold"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span>{item.label}</span>
              {item.to === "/agente" && noLeidos > 0 && (
                <span
                  className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-semibold text-white"
                  aria-label={`${noLeidos} sin leer`}
                  data-testid="nav-agente-no-leidos"
                >
                  {noLeidos > 99 ? "99+" : noLeidos}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-5 py-4 border-t border-slate-200">
        <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-slate-400">
          Desarrollado por Olimpo
        </p>
      </div>
    </div>
  );
}

function AppLayoutInterno() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { total: noLeidos } = useAvisosWa();  // PROMPT 145: punto verde en el boton del menu

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // PROMPT 144: la pantalla del agente (/agente) se usa como WhatsApp Web: todo el ancho y
  // todo el alto, sin el recuadro centrado de las demas paginas.
  const esAgente = location.pathname.startsWith("/agente");

  const currentTitle =
    NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== "/"))?.label ||
    "Inicio";

  return (
    <div
      className={`bg-background flex ${esAgente ? "h-screen overflow-hidden" : "min-h-screen"}`}
      style={esAgente ? { height: "100dvh" } : undefined}
    >
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-slate-200 fixed inset-y-0 left-0 z-30">
        <SidebarContent isAdmin={isAdmin} />
      </aside>

      <div className={`flex-1 lg:pl-64 flex flex-col min-w-0 ${esAgente ? "min-h-0" : ""}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                  <span className="relative">
                    <Menu className="h-5 w-5" />
                    {noLeidos > 0 && (
                      <span
                        className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white"
                        data-testid="mobile-menu-no-leidos"
                      />
                    )}
                  </span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <SidebarContent onNavigate={() => setMobileOpen(false)} isAdmin={isAdmin} />
              </SheetContent>
            </Sheet>
            <h1 className="font-heading text-lg font-semibold text-slate-900">{currentTitle}</h1>
          </div>

          <div className="flex items-center gap-3">
            <IntegracionesIndicator />
            <NotificationsBell />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium text-slate-800" data-testid="topbar-user-name">
                {user?.nombre}
              </span>
              <span className="text-[11px] uppercase tracking-wide text-slate-500">
                {user?.rol === "admin" ? "Administrador" : "Operador"}
              </span>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white text-sm font-semibold">
              {(user?.nombre || "?").charAt(0).toUpperCase()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </header>

        <main
          className={
            esAgente ? "flex-1 min-h-0 overflow-hidden p-0 lg:p-2 xl:p-3" : "flex-1 p-4 md:p-6 lg:p-8"
          }
        >
          <div className={esAgente ? "h-full min-h-0" : "max-w-7xl mx-auto"}>
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}


// PROMPT 145: los avisos de WhatsApp (numero de no leidos, sonido y avisos del sistema) se montan
// UNA vez, para administradores y operadores; una agencia no los recibe.
export default function AppLayout() {
  const { user } = useAuth();
  const equipo = user?.rol === "admin" || user?.rol === "operador";
  return equipo ? (
    <AvisosWhatsApp>
      <AppLayoutInterno />
    </AvisosWhatsApp>
  ) : (
    <AppLayoutInterno />
  );
}
